import { gql, Resolver } from '@apollo/client';
import axios, { AxiosResponse } from 'axios';
import Papa from 'papaparse';
import HttpStatus from 'http-status-codes';

interface StatementPerformanceDataInput {
    statementId: string;
    workTitle?: string;
}

interface GetStatementPerformanceDataArgs {
    token: string;
    inputs: StatementPerformanceDataInput[];
    trqkDirectoryUrl: string;
}

/**
 * @typedef {GetStatementPerformanceDataResult}
 * @property {RawPerformance[]} performances array of all raw performance data objects, flattened
 * from a list of the results of each statementId that was provided. In other words, the final
 * `performances` data maintains the order of the `statementIds` argument.
 * @property {GetStatementPerformanceDataError[]} errors a list of each statementId that failed to
 * generate data, and the reason why
 */
interface GetStatementPerformanceDataResult {
    performances: RawPerformance[];
    errors: GetStatementPerformanceDataError[];
}

const parsePerformanceDataFromResponse = (
    response: AxiosResponse<GetTrqkStatementPerformancesResponse>,
): RawPerformance[] => {
    if (response?.data?.errors) {
        throw new Error(
            `Error(s) in GraphQL query: \n${JSON.stringify(
                response.data.errors,
                null,
                4,
            )}`,
        );
    }

    const csvString =
        response?.data?.data?.getTrqkStatementPerformances?.data ?? null;

    if (csvString === null) {
        throw new Error(
            'Received empty data from getTrqkStatementPerformances',
        );
    }

    // Parse CSV string into a list of objects
    const result = Papa.parse<RawPerformance>(csvString, {
        newline: '/n',
        header: true,
    });

    const data = result?.data ?? ([] as RawPerformance[]);

    return data.filter(
        (rawPerformance: RawPerformance): boolean =>
            (rawPerformance?.['Work Title']?.length ?? 0) > 0,
    );
};

/**
 * Fetches the performance data for a list of `statementIds` and returns the entire set of data as
 * one concatenated list. Since performance data sets can be quite long, and the size of an AppSync
 * return value is limited, the API returns the data in CSV formatted as a single string.
 *
 * The query accepts a single statementId, but we may need to fetch more than one, depending on the
 * statement selection state.
 * @param obj
 * @param {GetStatementPerformanceDataArgs} args
 * @returns {GetStatementPerformanceDataResult}
 */
export const getStatementPerformanceData: Resolver = async (
    obj: Record<string, unknown>,
    args: GetStatementPerformanceDataArgs,
): Promise<GetStatementPerformanceDataResult | Error> => {
    const { inputs, token, trqkDirectoryUrl } = args;

    /**
     * Return a Promise that fetches the raw CSV data of the performances for a single statementId,
     * then parses the data into an array of objects (one object per row in the CSV). The data is
     * persisted in the performanceMap so it can be concatenated in the order of the statementIds
     * that were passed in to the resolver.
     * NOTE: There is an upper limit on how many rows of data can be returned by
     * getTrqkStatementPerformances because of how data persistence is implemented. When passing in
     * a workTitle, we are able to hide this issue to some degree because the row limitation is less
     * likely to be a problem with a filtered query.
     * @param statementId - a statement ID
     * @param workTitle - an optional work title (usually set from a URL search parameter)
     */
    const fetchPerformanceData = ({
        statementId,
        workTitle,
    }: StatementPerformanceDataInput): Promise<FetchPerformanceDataResult> =>
        new Promise<FetchPerformanceDataResult>(resolve => {
            axios({
                method: 'post',
                url: trqkDirectoryUrl,
                headers: {
                    Authorization: token,
                },
                // throw an error for non-200 status
                validateStatus: (status: number): boolean =>
                    status === HttpStatus.OK,
                data: {
                    query: `
                            query getTrqkStatementPerformances($statement_id: ID!, $work_title: String) {
                                getTrqkStatementPerformances(statement_id: $statement_id, work_title: $work_title) {
                                    data
                                }
                            }
                        `,
                    variables: {
                        statement_id: statementId,
                        work_title: workTitle,
                    },
                },
            })
                .then(
                    (
                        response: AxiosResponse<GetTrqkStatementPerformancesResponse>,
                    ) => {
                        const data = parsePerformanceDataFromResponse(response);

                        resolve({
                            statementId,
                            data,
                        });
                    },
                )
                .catch((error: Error) => {
                    // Resolve with an object that includes the statementId and error so that we
                    // can display some feedback to the user
                    resolve({
                        statementId,
                        error,
                    });
                });
        });

    // Fetch all performance data. We use promise.allSettled here so that requests are fired in
    // parallel and every request is sent even if one of them fails.
    const allPromiseResults = await Promise.allSettled(
        inputs.map(
            (
                input: StatementPerformanceDataInput,
            ): Promise<FetchPerformanceDataResult> =>
                fetchPerformanceData(input),
        ),
    );

    const output = {
        performances: [] as RawPerformance[],
        errors: [] as GetStatementPerformanceDataError[],
    };

    allPromiseResults.forEach((result): void => {
        if (result.status === 'fulfilled') {
            const data = result.value?.data ?? null;
            const error = result.value?.error ?? null;
            const statementId = result.value?.statementId ?? null;

            if (data) {
                output.performances = output.performances.concat(data);
            } else if (error && statementId) {
                output.errors.push({
                    statementId,
                    error,
                });
            }
        }
    });

    return output;
};

export const GET_STATEMENT_PERFORMANCE_DATA = gql`
    query GetStatementPerformanceData(
        $token: String!
        $inputs: [StatementPerformanceDataInput]!
        $trqkDirectoryUrl: String!
    ) {
        getStatementPerformanceData(
            token: $token
            inputs: $inputs
            trqkDirectoryUrl: $trqkDirectoryUrl
        ) @client
    }
`;
